/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';

import {
  Drawer,
  PrimaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { Box, CircularProgress, Divider } from '@mui/material';
import { FormProvider, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  useResetUserQueries,
  useStoreImage,
  useUpdateUser,
  useUserDetailsForm,
} from '../hooks';
import { useGetUser } from '../hooks/use-get-user';
import { isUserFormValuesChanged, setLicences } from '../store';
import { UserDetails } from '../types';

import { FormLayout } from './FormLayout';
import { UserDetailsForm } from './UserDetailsForm';
import { UserImages } from './UserImages';
import { UserLicences } from './UserLicences';

interface UserDrawerProps {
  onClose: (runQuery?: boolean) => void;
  open: boolean;
  user: UserDetails;
}

type PartialUser = Partial<UserDetails>;

export const UserDrawer = ({ onClose, open, user }: UserDrawerProps) => {
  const [selectedLicences, setSelectedLicences] = useState<
    UserDetails['licences']
  >([]);
  const form = useUserDetailsForm();
  const { mutate: updateUser } = useUpdateUser();
  const { mutate: storeImage } = useStoreImage(user?.uuid);

  const {
    isLoading,
    isFetching,
    data: userProfile,
  } = useGetUser({
    reset: form.reset,
    user,
    open,
  });

  const updateAccess = (
    mainArray: UserDetails['licences'],
    accessArray: UserDetails['licences'],
  ) => {
    const accessIds = new Set(accessArray.map((item) => item.id));

    return mainArray.map((item) => ({
      ...item,
      has_access: accessIds.has(item.id),
    }));
  };

  const resetUserQueries = useResetUserQueries();
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => onClose();

  const watchedValues = useWatch({ control: form.control });

  const { profilePic, signaturePic, ...userValues } = watchedValues;

  const hasFormValuesChanged = isUserFormValuesChanged(
    userValues as UserDetails,
  );
  const hasNewImages =
    profilePic instanceof File || signaturePic instanceof File;

  const hasLicenseChanges = useMemo(() => {
    if (!userProfile?.licences) return false;

    const originalSelected = userProfile.licences.filter((l) => l.has_access);
    const originalIds = new Set(originalSelected.map((l) => l.id));
    const currentIds = new Set(selectedLicences.map((l) => l.id));

    return (
      originalIds.size !== currentIds.size ||
      ![...originalIds].every((id) => currentIds.has(id))
    );
  }, [userProfile?.licences, selectedLicences]);

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!hasFormValuesChanged && !hasNewImages && !hasLicenseChanges) {
      handleClose();
      return;
    }

    try {
      setIsSaving(true);

      const formData = new FormData();
      const updatedValues = { ...values };

      formData.append('uuid', user?.uuid);

      if (values.profilePic instanceof File) {
        formData.append('profile_pic', values.profilePic);
        delete updatedValues.profilePic;
      }

      if (values.signaturePic instanceof File) {
        formData.append('signature_pic', values.signaturePic);
        delete updatedValues.signaturePic;
      }

      const saveImageAsync = (formData: FormData) =>
        new Promise((resolve, reject) => {
          storeImage(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        });

      if (formData.has('profile_pic') || formData.has('signature_pic')) {
        if (hasNewImages) {
          await saveImageAsync(formData);

          form.setValue('profilePic', '');
          form.setValue('signaturePic', '');
        }
      }

      const saveProfileAsync = (updatedValues: PartialUser) =>
        new Promise((resolve, reject) => {
          updateUser(updatedValues, {
            onSuccess: resolve,
            onError: reject,
          });
        });

      if (hasFormValuesChanged || hasLicenseChanges) {
        const payload = {
          uuid: user.uuid,
          first_name: updatedValues.firstName,
          last_name: updatedValues.lastName,
          position: updatedValues.companyPosition,
          position_type: Number(updatedValues.positionType),
          chargeable_rate: Number(updatedValues.chargeableRate),
          quals: updatedValues.personalQual,
          address: updatedValues.address,
          suburb: updatedValues.suburb,
          state: updatedValues.state,
          postcode: updatedValues.postCode,
          phone_number: updatedValues.phoneNumber,
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        payload.licences = updateAccess(
          userProfile?.licences || [],
          selectedLicences,
        );

        await saveProfileAsync(payload as PartialUser);
      }
      resetUserQueries();
      toast.success('Profile saved successfully!');
      handleClose();
    } catch (error) {
      toast.error('Error saving user profile');
    } finally {
      setIsSaving(false);
    }
  });

  const renderContent = () => {
    if (isLoading)
      return (
        <Box
          sx={{
            height: '100%',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      );

    return (
      <FormLayout
        onSubmit={handleSubmit}
        sx={{
          boxShadow: 'none !important',
          padding: '.5em',
        }}
      >
        <FormProvider {...form}>
          <UserDetailsForm />
          <Divider sx={{ marginY: '1em' }} />
          <UserImages user={userProfile} />
          <Divider sx={{ marginY: '1em' }} />
          <UserLicences
            loading={isFetching}
            licences={userProfile?.licences}
            onSelect={(license) => {
              setSelectedLicences(license);
              setLicences(license);
            }}
          />
        </FormProvider>
      </FormLayout>
    );
  };

  return (
    <Drawer
      sx={{ width: '900px !important' }}
      title={<Typography>Edit User</Typography>}
      onClose={handleClose}
      open={open}
      content={renderContent()}
      actions={
        !isLoading && (
          <Box display="flex" justifyContent="flex-end" width="100%">
            <PrimaryButton onClick={handleSubmit} disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Done'}
            </PrimaryButton>
          </Box>
        )
      }
    />
  );
};
